import { Box, Button, TextField, Typography, Accordion, AccordionSummary, AccordionDetails, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, CircularProgress } from '@mui/material';
import { Pen, CaretDown } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import KitModal from '../UI/KitModal';
import { useAuthStore } from '../../store/storeAuth';
import SearchableSelect from '../SearchableSelect';
import { UserService } from '../../service/UserService';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import TagManager from 'react-gtm-module';
import { FlowService } from '../../service/FlowService';
import { useNotificationStore } from '../../store/storeNotifications';
import useFlowsStore from '../../store/storeFlows';
import { useNavigate } from 'react-router-dom';
import { FolderService } from '../../service/FolderService';
import { allNodesTargetBasedOnType } from '../../util/dataStoredToType';
import { convertShorthandToReactFlow } from '../CreateShorthandFlowModal';
import { FluxService } from '../../service/FluxService';

// import { GTMDataLayerEvent } from 'react-gtm-module'; // Adjust the import path as needed

// Local declaration for ReactFlow within CreateFlowModal
interface ReactFlow {
  currentFlowName?: string;
  getCurrentFlowName?: () => string;
}

// Extend the Window interface locally
declare global {
  interface Window {
    ReactFlow?: ReactFlow;
    // dataLayer?: any[]
  }
}

export const NODE_TYPE_MAPPING: { [key: string]: string } = {
  GS: 'globalStarter',
  ATG: 'commandTextNode',
  TB: 'textBox',
  RTB: 'richTextBox',
  DB: 'outputController',
  SN: 'stickyNote',
  AiIG: 'imageGenerator',
  AiIR: 'imageReaderNode',
  AiVG: 'animationNode',
  AiAT: 'audioReaderNode',
  AiMG: 'commandMusicNode',
  PLD: 'pullData',
  PSD: 'pushData',
  WS: 'webSearch',
  WSCP: 'webCrawling',
  SeqN: 'arrayNode',
  Func: 'mathFunction',
  Script: 'scripting',
  CN: 'conditionNode',
  DI: 'varInputNode',
  DO: 'outputObjectNode',
  FC: 'fluxBox',
};

type Props = {
  onHide: () => void;
  onCreateFlow: (
    name: string,
    isEditing?: boolean,
    id?: string,
    folderId?: string,
    isCopy?: boolean,
    type?: string,
  ) => void;
  show: boolean;
  data: {
    isSharing: boolean;
    isEditing: boolean;
    type?: string;
    flow: {
      id: string;
      name: string;
    };
    folderId: string;
  };
  onShareFlow: (
    id: string,
    email: string,
    name: string,
    selectedCompanyId?: string,
  ) => void;
  theme: string | undefined;
  setIsFlowModalOpen: any;
};

const CreateFlowModal = ({
  onHide,
  onCreateFlow,
  show,
  data,
  onShareFlow,
}: Props) => {
  const { user, folders, setFolders, setAuthUser } = useAuthStore(state => state);
  const { setNotification } = useNotificationStore(state => state);
  const { addFlow } = useFlowsStore(state => state);
  const navigate = useNavigate();

  const [name, setName] = useState(data?.flow?.name || '');
  const [email, setEmail] = useState('');
  const [initial, setInitial] = useState(true);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    undefined | string
  >();
  
  const [expandCreateWithAi, setExpandCreateWithAi] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [isCreatingFromShorthand, setIsCreatingFromShorthand] = useState(false);

  useDebounceEffect(
    () => {
      const getCompanies = async () => {
        const res = await UserService.getCompanies(email);

        if (res.data.length === 1) {
          setSelectedCompanyId(res.data[0].id);
        }

        setCompanies(res.data);
      };

      if (initial && !email) {
        getCompanies();

        setInitial(false);
      } else if (email) {
        getCompanies();
      }
    },
    [email, initial],
    400,
  );

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    if (expandCreateWithAi && userPrompt) {
      handleShorthandSubmit();
      return;
    }
    
    onHide();
    setName('');

    TagManager.dataLayer({
      dataLayer: {
        event: 'flow_creation',
        flowName: name,
      } as any,
    });

    if (data?.isEditing) {

      const typeToPass = data?.type || 'flow';
      onCreateFlow(name, true, data?.flow?.id, data?.folderId, false, typeToPass);
    } else {
      onCreateFlow(name, false, '', data?.folderId, false, data?.type);
    }
  };

  const handleShorthandSubmit = async () => {
    if (!name.trim()) {
      setNotification({
        message: 'Flow name is required',
        type: 'error',
      });
      return;
    }

    setIsCreatingFromShorthand(true);
    
    try {
        const autoCreatorFlowId = process.env.REACT_APP_AUTO_CREATOR_FLOW_ID || ''; 
        
        if (!autoCreatorFlowId) {
          setNotification({
            message: 'Auto creator flow ID is not configured',
            type: 'error',
          });
          setIsCreatingFromShorthand(false);
          return;
        }
        
        const variableInputs = [
          {
            inputId: "varInputNode_1749483673977_0.5071",
            inputText: userPrompt, 
            links: [],
            files: []
          }
        ];

        const autoCreatorResponse = await FluxService.FluxApiProduction(
          autoCreatorFlowId,
          { variableInputs }
        );
        
        
        if (autoCreatorResponse?.data?.message && Array.isArray(autoCreatorResponse.data.message)) {
          const processedOutput = autoCreatorResponse.data.message.find(
            (output: any) => output.outputId && output.text && output.text.trim() !== ''
          );
          
          if (!processedOutput || !processedOutput.text) {
            throw new Error('Auto creator did not return valid output');
          }
          
          let generatedShorthandJson = processedOutput.text;
          
          try {
            const parsedJson = JSON.parse(generatedShorthandJson);

            if (!parsedJson.nodes || !Array.isArray(parsedJson.nodes) || 
                !parsedJson.edges || !Array.isArray(parsedJson.edges)) {
              throw new Error('Generated JSON is missing required nodes or edges structure');
            }
            
            
            const createResponse = await FlowService.createFlow({
              name: name,
              folderId: data?.folderId,
              type: data?.type,
            });

            const flowData = convertShorthandToReactFlow(parsedJson);
            await FlowService.nodesAutomation(createResponse.data.id, flowData);
            
            addFlow({
              id: createResponse.data.id,
              name: createResponse.data.name,
              index: createResponse.data.index,
            });
            
            if (data?.type === 'chat' && user) {
              setAuthUser({
                ...user,
                chatsId: [...(user.chatsId || []), createResponse.data.id],
              });
            }
            
            setNotification({
              message: 'Flow created successfully',
              type: 'success',
            });
            
            setName('');
            setUserPrompt('');
            setExpandCreateWithAi(false);
            setIsCreatingFromShorthand(false);
            
            onHide();
            
            setTimeout(() => {
              navigate(`/flow/${createResponse.data.id}`);
            }, 100);
          } catch (jsonError) {
            console.error('Error with generated JSON:', jsonError);
            throw new Error('Auto creator did not return valid JSON');
          }
        } else {
          throw new Error('Auto creator response format is invalid');
        }
      
    } catch (error) {
      console.error('Error in flow creation process:', error);
      
      let errorMessage = 'Failed to create flow';
      if (error && (error as any).response && (error as any).response.data && (error as any).response.data.message) {
        errorMessage = (error as any).response.data.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setNotification({
        message: errorMessage,
        type: 'error',
      });
      setIsCreatingFromShorthand(false);
    }
  };

  const companyOptions = companies.map((company: any) => ({
    label: company.name,
    value: company.id,
  }));

  const handleUserPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserPrompt(event.target.value);
  };

  return (
    <KitModal
      onHide={onHide}
      show={show}
      sx={{
        '& .MuiPaper-root': {
          maxWidth: expandCreateWithAi ? '700px' : '650px',
          width: '100%',
        },
      }}
      title={
        <Box>
          {data?.isEditing
            ? 'Rename Flow'
            : data?.type === 'chat'
            ? 'Create Chat Template'
            : data.isSharing
            ? 'Share Flow'
            : expandCreateWithAi
            ? 'Create Flow with Ai'
            : 'Create Flow'}
        </Box>
      }
    >
      <form onSubmit={handleFormSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap="1rem"
        >
          <TextField
            id="outlined-basic"
            label="Enter flow name"
            variant="outlined"
            type="text"
            name="name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
          />

          {!data?.isEditing && !data?.isSharing && (
            <Accordion 
              expanded={expandCreateWithAi}
              onChange={() => setExpandCreateWithAi(!expandCreateWithAi)}
              sx={{
                boxShadow: 'none',
                '&:before': {
                  display: 'none',
                },
                width: '100%'
              }}
            >
              <AccordionSummary
                expandIcon={<CaretDown />}
                aria-controls="shorthand-content"
                id="shorthand-header"
                sx={{
                  padding: 0,
                  minHeight: '36px',
                  maxHeight: '36px',
                  height: '36px',
                  overflow: 'hidden',
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(106, 117, 255, 0.12)',
                    cursor: 'pointer',
                    '& .MuiTypography-root': {
                      fontWeight: 'bold'
                    }
                  },
                  pl: 1.5,
                  pr: 1.5,
                  '&.Mui-expanded': {
                    minHeight: '36px',
                    maxHeight: '36px',
                    height: '36px'
                  }
                }}
              >
                <Typography color="primary">
                  Create with Ai
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pt: 2, mt: 1 }}>
                <Box display="flex" flexDirection="column" gap={2} width="100%">
                  <TextField
                    name="userPrompt"
                    value={userPrompt}
                    onChange={handleUserPromptChange}
                    fullWidth
                    multiline
                    rows={12}
                    label="Describe the flow you want to create"
                    placeholder="Describe what you want your flow to do. For example: 'Create a flow that takes a text input, generates an image based on it, and then responds with a summary.'"
                    sx={{ 
                      width: '100%',
                      '& .MuiInputBase-input': {
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {data?.flow?.id && data.isSharing && (
            <Box sx={{ width: '100%' }}>
              <TextField
                id="outlined-basic"
                variant="outlined"
                type="text"
                name="email"
                fullWidth
                value={email}
                onChange={e => setEmail(e.target.value)}
                label="Enter email or space ID"
              />

              <Box sx={{ height: '10px' }}></Box>
              {companies.length > 1 && (
                <SearchableSelect
                  fullWidth
                  options={companyOptions}
                  label="Select Environment"
                  onChange={value => {
                    const selectedValue = value ? value.value : '';

                    setSelectedCompanyId(selectedValue);
                  }}
                  value={
                    companyOptions.find(
                      option => option?.value === selectedCompanyId,
                    ) || null
                  }
                  className="nodrag"
                  style={{ width: '100%', marginBottom: '20px' }}
                  sx={{
                    '& .MuiList-root': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                    },
                    '& .MuiSvgIcon-root': {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: 'none',
                    '.MuiOutlinedInput-notchedOutline': { border: 0 },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                />
              )}

              <br />

              <Box
                display={'flex'}
                mt={'15px'}
                alignItems={'center'}
                gap={'10px'}
                className="d-flex justify-content-center gap-3 mt-3 align-items-center"
              >
                <Button
                  variant={`contained`}
                  size="small"
                  disabled={!email || !name || !data.isSharing}
                  onClick={() => {
                    onShareFlow(data?.flow?.id, email, name, selectedCompanyId);
                  }}
                >
                  Share Flow
                </Button>
              </Box>
            </Box>
          )}

          {!data?.isSharing && (
            <Box alignSelf="flex-end">
              <Button
                disabled={!name || (expandCreateWithAi && !userPrompt)}
                onClick={handleFormSubmit}
                variant={`contained`}
                size="small"
              >
                {isCreatingFromShorthand ? (
                  <CircularProgress size={24} color="inherit" />
                ) : data?.isEditing ? (
                  <Pen />
                ) : (
                  'Create'
                )}
              </Button>
            </Box>
          )}
        </Box>
      </form>
    </KitModal>
  );
};

export default CreateFlowModal;
